<ion-header [translucent]="true">
  <ion-toolbar>
    <ion-buttons slot="start">
      <ion-button (click)="goBack()">
        <ion-icon name="arrow-back" slot="icon-only"></ion-icon>
      </ion-button>
    </ion-buttons>
    <ion-title>Dimensional Analysis</ion-title>
    <ion-buttons slot="end">
      <ion-button
        (click)="exportData()"
        [disabled]="isLoading || !analysisData"
      >
        <ion-icon name="download-outline" slot="start"></ion-icon>
        Export
      </ion-button>
      <ion-button (click)="onRefresh()" [disabled]="isLoading">
        <ion-icon name="refresh-outline" slot="icon-only"></ion-icon>
      </ion-button>
    </ion-buttons>
  </ion-toolbar>
</ion-header>

<ion-content [fullscreen]="true">
  <ion-grid style="margin: 3px">
    <!-- Error State -->
    <ion-row *ngIf="error">
      <ion-col>
        <ion-item color="danger">
          <ion-icon name="warning-outline" slot="start"></ion-icon>
          {{ error }}
        </ion-item>
      </ion-col>
    </ion-row>

    <!-- Loading State -->
    <ion-row
      *ngIf="isLoading"
      class="ion-justify-content-center"
      style="padding: 3rem"
    >
      <ion-col size="auto" class="ion-text-center">
        <ion-spinner color="primary"></ion-spinner>
        <p style="margin-top: 1rem">Loading dimensional analysis...</p>
      </ion-col>
    </ion-row>

    <!-- Dimensional Analysis Content -->
    <div *ngIf="!isLoading && !error && analysisData">
      <!-- Dimension Overview -->
      <ion-row>
        <ion-col>
          <ion-card>
            <ion-card-header>
              <ion-card-title
                >{{ analysisData.dimensionName }} Analysis</ion-card-title
              >
              <ion-card-subtitle
                >{{ analysisData.strategy | uppercase }} strategy classification
                performance</ion-card-subtitle
              >
            </ion-card-header>
            <ion-card-content>
              <ion-grid>
                <ion-row class="ion-text-center">
                  <ion-col>
                    <div
                      style="
                        font-size: 2rem;
                        font-weight: 700;
                        line-height: 1.2;
                      "
                    >
                      {{ analysisData.totalClassifications | number }}
                    </div>
                    <div style="font-size: 0.875rem; margin-top: 0.25rem">
                      Total Classifications
                    </div>
                  </ion-col>
                  <ion-col>
                    <div
                      style="
                        font-size: 2rem;
                        font-weight: 700;
                        line-height: 1.2;
                      "
                    >
                      {{ analysisData.avgConfidence * 100 | number: "1.1-1" }}%
                    </div>
                    <div style="font-size: 0.875rem; margin-top: 0.25rem">
                      Average Confidence
                    </div>
                  </ion-col>
                  <ion-col>
                    <div
                      style="
                        font-size: 2rem;
                        font-weight: 700;
                        line-height: 1.2;
                      "
                    >
                      {{ analysisData.lowConfidenceArticles.length }}
                    </div>
                    <div style="font-size: 0.875rem; margin-top: 0.25rem">
                      Low Confidence Articles
                    </div>
                  </ion-col>
                </ion-row>
              </ion-grid>

              <div style="margin-top: 1.5rem; text-align: center">
                <ion-chip [color]="getOverallSystemHealthColor()" size="large">
                  <ion-icon name="analytics-outline" slot="start"></ion-icon>
                  System Health: {{ getOverallSystemHealth() }}
                </ion-chip>
              </div>
            </ion-card-content>
          </ion-card>
        </ion-col>
      </ion-row>

      <!-- Two-column layout for confidence distribution and method distribution -->
      <ion-row>
        <!-- Confidence Distribution -->
        <ion-col size="12" size-md="6">
          <ion-card>
            <ion-card-header>
              <ion-card-title>Confidence Distribution</ion-card-title>
              <ion-card-subtitle
                >Quality breakdown of all classifications</ion-card-subtitle
              >
            </ion-card-header>
            <ion-card-content>
              <div
                *ngIf="analysisData.confidenceDistribution.length === 0"
                style="text-align: center; padding: 2rem"
              >
                <ion-icon
                  name="bar-chart-outline"
                  style="font-size: 2rem; opacity: 0.5; margin-bottom: 1rem"
                ></ion-icon>
                <p style="margin: 0">
                  No confidence distribution data available.
                </p>
              </div>

              <div *ngIf="analysisData.confidenceDistribution.length > 0">
                <div
                  *ngFor="
                    let item of analysisData.confidenceDistribution;
                    trackBy: trackByDistribution
                  "
                  style="
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 0.75rem;
                    margin-bottom: 0.5rem;
                    border: 1px solid var(--ion-color-light);
                    border-radius: 8px;
                  "
                >
                  <div>
                    <span style="font-weight: 500; font-size: 0.9rem">{{
                      item.range
                    }}</span>
                  </div>
                  <div
                    style="
                      text-align: right;
                      display: flex;
                      align-items: center;
                      gap: 0.5rem;
                    "
                  >
                    <div>
                      <div style="font-weight: 600">
                        {{ item.count | number }}
                      </div>
                      <div style="font-size: 0.8rem; opacity: 0.7">
                        {{ item.percentage }}%
                      </div>
                    </div>
                    <ion-chip
                      [color]="getDistributionColor(item.range)"
                      size="small"
                    >
                      {{ item.percentage }}%
                    </ion-chip>
                  </div>
                </div>
              </div>
            </ion-card-content>
          </ion-card>
        </ion-col>

        <!-- Method Distribution -->
        <ion-col size="12" size-md="6">
          <ion-card>
            <ion-card-header>
              <ion-card-title>Classification Methods</ion-card-title>
              <ion-card-subtitle
                >Performance by classification approach</ion-card-subtitle
              >
            </ion-card-header>
            <ion-card-content>
              <div
                *ngIf="analysisData.methodDistribution.length === 0"
                style="text-align: center; padding: 2rem"
              >
                <ion-icon
                  name="settings-outline"
                  style="font-size: 2rem; opacity: 0.5; margin-bottom: 1rem"
                ></ion-icon>
                <p style="margin: 0">No method distribution data available.</p>
              </div>

              <div *ngIf="analysisData.methodDistribution.length > 0">
                <div
                  *ngFor="
                    let method of analysisData.methodDistribution;
                    trackBy: trackByMethod
                  "
                  style="
                    margin-bottom: 1.5rem;
                    border: 1px solid var(--ion-color-light);
                    border-radius: 8px;
                    padding: 1rem;
                  "
                >
                  <div
                    style="
                      display: flex;
                      justify-content: space-between;
                      align-items: center;
                      margin-bottom: 0.75rem;
                    "
                  >
                    <div
                      style="display: flex; align-items: center; gap: 0.5rem"
                    >
                      <ion-chip
                        [color]="getMethodColor(method.method)"
                        size="medium"
                      >
                        {{ method.method.toUpperCase() }}
                      </ion-chip>
                    </div>
                    <ion-chip
                      [color]="getConfidenceColor(method.avgConfidence)"
                      size="small"
                    >
                      {{ method.avgConfidence * 100 | number: "1.1-1" }}% avg
                    </ion-chip>
                  </div>

                  <div
                    style="
                      display: flex;
                      justify-content: space-between;
                      font-size: 0.9rem;
                    "
                  >
                    <span>{{ method.count | number }} classifications</span>
                    <span>{{ method.percentage }}% of total</span>
                  </div>
                </div>
              </div>
            </ion-card-content>
          </ion-card>
        </ion-col>
      </ion-row>

      <!-- Low Confidence Articles -->
      <ion-row>
        <ion-col>
          <ion-card>
            <ion-card-header>
              <ion-card-title>Articles Needing Attention</ion-card-title>
              <ion-card-subtitle
                >Articles with low classification confidence ({{
                  analysisData.lowConfidenceArticles.length
                }}
                total)</ion-card-subtitle
              >
            </ion-card-header>
            <ion-card-content>
              <div
                *ngIf="analysisData.lowConfidenceArticles.length === 0"
                style="text-align: center; padding: 2rem"
              >
                <ion-icon
                  name="checkmark-circle-outline"
                  style="
                    font-size: 2rem;
                    color: var(--ion-color-success);
                    margin-bottom: 1rem;
                  "
                ></ion-icon>
                <p style="margin: 0; color: var(--ion-color-success)">
                  Great! No articles have low classification confidence.
                </p>
              </div>

              <div
                *ngIf="analysisData.lowConfidenceArticles.length > 0"
                style="max-height: 400px; overflow-y: auto"
              >
                <div
                  *ngFor="
                    let article of analysisData.lowConfidenceArticles;
                    trackBy: trackByArticle
                  "
                  style="
                    margin-bottom: 1rem;
                    border: 1px solid var(--ion-color-light);
                    border-radius: 8px;
                    padding: 1rem;
                  "
                >
                  <div
                    style="
                      display: flex;
                      justify-content: space-between;
                      align-items: center;
                      margin-bottom: 0.5rem;
                    "
                  >
                    <div>
                      <h4 style="margin: 0; font-size: 1rem; font-weight: 600">
                        {{ article.articleTitle }}
                      </h4>
                      <div
                        style="
                          display: flex;
                          gap: 0.5rem;
                          margin-top: 0.25rem;
                          font-size: 0.8rem;
                        "
                      >
                        <button
                          (click)="viewArticleAnalysis(article.articleId)"
                          style="
                            background: none;
                            border: none;
                            color: var(--ion-color-primary);
                            text-decoration: underline;
                            cursor: pointer;
                            padding: 0;
                          "
                        >
                          analyze
                        </button>
                        <span style="color: var(--ion-color-medium)">•</span>
                        <a
                          [href]="getArticleUrl(article.articleId)"
                          target="_blank"
                          style="
                            color: var(--ion-color-secondary);
                            text-decoration: underline;
                          "
                        >
                          edit
                        </a>
                      </div>
                    </div>
                    <ion-chip
                      [color]="getConfidenceColor(article.primaryConfidence)"
                      size="small"
                    >
                      {{ article.primaryConfidence * 100 | number: "1.1-1" }}%
                    </ion-chip>
                  </div>

                  <div
                    style="
                      display: flex;
                      justify-content: space-between;
                      font-size: 0.9rem;
                      opacity: 0.8;
                    "
                  >
                    <span
                      >Category:
                      <strong>{{ article.primaryCategory }}</strong></span
                    >
                    <span>{{ article.chunkCount }} chunks</span>
                  </div>
                </div>
              </div>
            </ion-card-content>
          </ion-card>
        </ion-col>
      </ion-row>

      <!-- Recent Classification Trends -->
      <ion-row>
        <ion-col>
          <ion-card>
            <ion-card-header>
              <ion-card-title>Recent Activity (Last 7 Days)</ion-card-title>
              <ion-card-subtitle
                >Classification trends and performance over
                time</ion-card-subtitle
              >
            </ion-card-header>
            <ion-card-content>
              <div
                *ngIf="analysisData.recentClassifications.length === 0"
                style="text-align: center; padding: 2rem"
              >
                <ion-icon
                  name="calendar-outline"
                  style="font-size: 2rem; opacity: 0.5; margin-bottom: 1rem"
                ></ion-icon>
                <p style="margin: 0">
                  No recent classification activity found.
                </p>
              </div>

              <div *ngIf="analysisData.recentClassifications.length > 0">
                <div
                  *ngFor="
                    let day of analysisData.recentClassifications;
                    trackBy: trackByClassification
                  "
                  style="
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 0.75rem;
                    margin-bottom: 0.5rem;
                    border: 1px solid var(--ion-color-light);
                    border-radius: 8px;
                  "
                >
                  <div>
                    <div style="font-weight: 600; font-size: 0.95rem">
                      {{ formatDate(day.date) }}
                    </div>
                    <div
                      style="
                        font-size: 0.8rem;
                        opacity: 0.7;
                        margin-top: 0.25rem;
                      "
                    >
                      {{ formatDateAgo(day.date) }}
                    </div>
                  </div>

                  <div style="text-align: center">
                    <div style="font-weight: 600">
                      {{ day.classificationsCount | number }}
                    </div>
                    <div style="font-size: 0.8rem; opacity: 0.7">
                      classifications
                    </div>
                  </div>

                  <div style="text-align: center">
                    <div style="font-weight: 600">
                      {{ day.avgConfidence * 100 | number: "1.1-1" }}%
                    </div>
                    <div style="font-size: 0.8rem; opacity: 0.7">
                      avg confidence
                    </div>
                  </div>

                  <div style="text-align: right">
                    <ion-chip color="medium" size="small">
                      {{ day.newCategories }} categories
                    </ion-chip>
                  </div>
                </div>
              </div>
            </ion-card-content>
          </ion-card>
        </ion-col>
      </ion-row>
    </div>
  </ion-grid>
</ion-content>
